"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.icpTextKeyOf = icpTextKeyOf;
exports.icpReadStateOf = icpReadStateOf;
exports.icpValuesOf = icpValuesOf;
exports.icpRankOf = icpRankOf;
exports.icpRanksOf = icpRanksOf;
exports.compareIcpRanks = compareIcpRanks;
exports.profileTopLinesOf = profileTopLinesOf;
exports.profileBreakdownOf = profileBreakdownOf;
exports.icpReadingOf = icpReadingOf;
const icp_1 = require("./icp");
const icp_scoring_1 = require("./icp-scoring");
// ADR 0062 / docs/PLAN-icp-main-ranking.md §2.2–§2.4 — the profile ranking, as pure functions the server (Package B) and the
// browser (Package D) share, so a power sort in the browser and the stored ranking can never disagree.
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const HEX_RADIX = 16;
const HASH_HEX_DIGITS = 8;
/** Thousands of rows hash the same few lines, so each line's words are hashed once (F23.04). */
const TEXT_KEYS = new Map();
const TEXT_KEYS_MAX = 5_000;
/**
 * FNV-1a hash (8 hex digits) of a line's words (`icpPartTextOf` for a Jev line, the text for English). A stored reading counts for a
 * line only while its `textKey` equals this, so a reworded line is "not read" until Jev reads it again.
 */
function icpTextKeyOf(text) {
    const cached = TEXT_KEYS.get(text);
    if (cached !== undefined)
        return cached;
    let hash = FNV_OFFSET_BASIS;
    for (let index = 0; index < text.length; index += 1) {
        hash = Math.imul(hash ^ text.charCodeAt(index), FNV_PRIME);
    }
    const key = (hash >>> 0).toString(HEX_RADIX).padStart(HASH_HEX_DIGITS, '0');
    if (TEXT_KEYS.size >= TEXT_KEYS_MAX)
        TEXT_KEYS.clear();
    TEXT_KEYS.set(text, key);
    return key;
}
/**
 * §2.3. `readsCvs`: the profile has a Jev-read line (skills, habits or English); without one nothing waits for Jev (`not_needed`).
 * `cv` is the CV state of the row (`none`/`unreadable` → `unreadable`; `coming` → `waiting`). A reading that failed
 * `ICP_READ_ATTEMPTS_MAX` times is `unreadable`; fewer is `waiting`. A readable CV with no provider is `not_connected`.
 * A finished reading wins over a missing provider: what Jev already read is not forgotten when the key goes.
 */
function icpReadStateOf(input) {
    const { readsCvs, cv, reading, isJevConnected } = input;
    if (!readsCvs)
        return 'not_needed';
    if (cv === 'none' || cv === 'unreadable')
        return 'unreadable';
    if (reading?.status === 'read')
        return 'read';
    if (reading?.status === 'unreadable')
        return 'unreadable';
    if (reading?.status === 'failed' && reading.attempts >= icp_1.ICP_READ_ATTEMPTS_MAX)
        return 'unreadable';
    if (cv === 'coming')
        return 'waiting';
    return isJevConnected ? 'waiting' : 'not_connected';
}
// ---- Values ----
function leftOut(detail) {
    return { credit: 0, isKnown: false, meetsMust: false, isExcluded: true, mark: 'not_read_yet', detail };
}
// Thousands of rows share these words, so they share the objects too (F23.04).
const NOT_READ_YET = leftOut('Not read yet');
const NOT_CONNECTED = leftOut('Jev is not connected');
const NO_CV = leftOut('No CV to read');
function leftOutBecauseOf(state) {
    if (state === 'not_connected')
        return NOT_CONNECTED;
    return state === 'unreadable' ? NO_CV : NOT_READ_YET;
}
function preparedOf(items) {
    return items.map((item) => ({ item, textKey: item.kind === 'jev' ? icpTextKeyOf((0, icp_1.icpPartTextOf)(item)) : null }));
}
function readingLineOf(reading, itemId, textKey) {
    for (const line of reading.lines) {
        if (line.itemId === itemId)
            return line.textKey === textKey ? line : null;
    }
    return null;
}
function valuesFrom(prepared, reading, state, facts, job) {
    const own = state === 'read' && reading?.status === 'read' ? reading : null;
    return prepared.map(({ item, textKey }) => {
        if (item.kind === 'fact')
            return (0, icp_scoring_1.icpFactValueOf)(item.fact, facts, job);
        if (own === null)
            return leftOutBecauseOf(state);
        if (item.kind === 'english')
            return (0, icp_scoring_1.icpEnglishValueOf)(own.english, true);
        const line = readingLineOf(own, item.id, textKey);
        return line === null ? NOT_READ_YET : (0, icp_scoring_1.icpJevValueOf)(line, true);
    });
}
/**
 * One `IcpItemValue` per line, in `items` order, by the value rules of ADR 0061 §5–§6 (unchanged). A Jev line whose reading is
 * missing or whose `textKey` no longer matches is excluded as "not read yet"; in any state but `read` the Jev lines are
 * excluded too. Facts are worked out from `facts` and `job` with `icpFactValueOf`.
 */
function icpValuesOf(items, reading, state, facts, job) {
    return valuesFrom(preparedOf(items), reading, state, facts, job);
}
// ---- Rank ----
const NO_MISSING_MUSTS = [];
function pendingRank() {
    return { match: null, fit: null, covers: 0, showsAllMusts: false, missingMusts: NO_MISSING_MUSTS, stars: null, tab: null, rankKey: null, isPending: true };
}
function needsALookRank(fit, covers) {
    return { match: null, fit, covers, showsAllMusts: false, missingMusts: NO_MISSING_MUSTS, stars: null, tab: 'needs_a_look', rankKey: icp_1.ICP_RANK_KEY_NEEDS_A_LOOK, isPending: false };
}
function starsOf(match, missingMustCount) {
    const stars = icp_1.ICP_STAR_FLOORS.find((step) => match >= step.from)?.stars ?? 1;
    if (missingMustCount === 0 || stars <= icp_1.ICP_MUST_MISSING_STARS_MAX)
        return stars;
    return icp_1.ICP_MUST_MISSING_STARS_MAX;
}
function tabOf(match, missingMustCount) {
    if (missingMustCount === 0 && match >= icp_1.ICP_TOP_FIT_MATCH)
        return 'top_fit';
    return match >= icp_1.ICP_MAYBE_MATCH ? 'maybe' : 'low_fit';
}
/**
 * §2.4: the match (after `penalty`, floored at 0), fit, covers, stars, tab and rank key. `waiting` has no tab, stars or match
 * and `isPending` true; `unreadable` is Needs a look with rank key `ICP_RANK_KEY_NEEDS_A_LOOK`; every other state is ranked by the
 * tab table. A missing must-have caps the stars at `ICP_MUST_MISSING_STARS_MAX`. `missingMusts` are the titles of unmet lines rated 5.
 * A person with nothing to judge (every line left out, e.g. a Jev-only profile and no key) is Needs a look too: a person has to look.
 */
function icpRankOf(items, values, state, penalty) {
    if (state === 'waiting')
        return pendingRank();
    if (state === 'unreadable')
        return needsALookRank(null, 0);
    const score = (0, icp_scoring_1.icpScoreOf)(items.map((item) => item.rating), values, true);
    if (score.match === null)
        return needsALookRank(score.fit, score.covers);
    const missingMusts = [];
    items.forEach((item, index) => {
        const value = values[index];
        if (item.rating === icp_1.ICP_MUST_RATING && value && !value.isExcluded && !value.meetsMust)
            missingMusts.push(item.text);
    });
    const match = Math.max(0, score.match - penalty);
    const tab = tabOf(match, missingMusts.length);
    const rankKey = icp_1.ICP_RANK_KEY_TAB_BASE[tab] + (missingMusts.length === 0 ? icp_1.ICP_RANK_KEY_MUSTS_BONUS : 0) + match;
    return { match, fit: score.fit, covers: score.covers, showsAllMusts: score.showsAllMusts, missingMusts, stars: starsOf(match, missingMusts.length), tab, rankKey, isPending: false };
}
/**
 * Every row at once, for the browser and for the server's re-rank: facts from `icpCandidateFactsOf(row, ctx.canSeeSalary)`, state from
 * `icpReadStateOf` (using `row.cv` and `row.profileReading`), values, rank (penalty `row.knockoutPenalty`). Keyed by application id.
 * 5,000 rows × 16 lines must take under 100 ms.
 */
function icpRanksOf(items, rows, ctx) {
    const prepared = preparedOf(items);
    const readsCvs = items.some(icp_1.isJevReadItem);
    const ranked = new Map();
    for (const row of rows) {
        const state = icpReadStateOf({ readsCvs, cv: row.cv, reading: row.profileReading, isJevConnected: ctx.isJevConnected });
        const values = valuesFrom(prepared, row.profileReading, state, (0, icp_scoring_1.icpCandidateFactsOf)(row, ctx.canSeeSalary), ctx.job);
        ranked.set(row.applicationId, { ...icpRankOf(items, values, state, row.knockoutPenalty), state, values });
    }
    return ranked;
}
/** Rank key high to low, a null key (Waiting) last, then import order. Returns 0 only for the same row. */
function compareIcpRanks(a, b) {
    if (a.rankKey !== b.rankKey) {
        if (a.rankKey === null)
            return 1;
        if (b.rankKey === null)
            return -1;
        return b.rankKey - a.rankKey;
    }
    return a.order - b.order;
}
// ---- What the screens show ----
function lineViewOf(item, value) {
    return {
        itemId: item.id,
        text: item.text,
        group: item.group,
        rating: item.rating,
        isMust: item.rating === icp_1.ICP_MUST_RATING,
        mark: value.mark,
        credit: value.credit,
        isKnown: value.isKnown,
        isExcluded: value.isExcluded,
        detail: value.detail,
    };
}
/** The first `count` lines worth showing a hiring manager (SHL-01): lines rated 5 first, then by rating; excluded lines left out. */
function profileTopLinesOf(items, values, count) {
    const lines = [];
    items.forEach((item, index) => {
        const value = values[index];
        if (value && !value.isExcluded)
            lines.push(lineViewOf(item, value));
    });
    // Array.sort is stable, so lines of one rating keep the profile's order.
    return lines.sort((a, b) => b.rating - a.rating).slice(0, Math.max(0, count));
}
/** The reasons for one person (SCR-01, CAND-03): the rank, the read state, the knockout `penalty` already taken off, and every line. */
function profileBreakdownOf(items, values, rank, state, penalty) {
    return {
        match: rank.match,
        fit: rank.fit,
        covers: rank.covers,
        stars: rank.stars,
        tab: rank.tab,
        state,
        showsAllMusts: rank.showsAllMusts,
        missingMusts: rank.missingMusts,
        penalty,
        lines: items.flatMap((item, index) => {
            const value = values[index];
            return value ? [lineViewOf(item, value)] : [];
        }),
    };
}
/**
 * The compact reading of one Jev person row for the profile's lines: for each `ask.icp.itemIds` entry that has a part, one
 * `IcpReadingLine` with `textKey = icpTextKeyOf(part.text)`. No quotes, no personal text. `attempts` is the failures so far.
 * A part Jev gave no answer for, on a person it did read, is a grade 0 line, which counts as unknown exactly as an empty answer did.
 */
function icpReadingOf(person, ask, attempts) {
    if (person.status !== 'read' || ask.icp === null)
        return { askId: ask.id, status: person.status, attempts, english: null, lines: [] };
    const lines = [];
    ask.icp.itemIds.forEach((itemId, index) => {
        const part = ask.parts[index];
        if (!part)
            return;
        const answer = person.answers[index];
        lines.push({ itemId, textKey: icpTextKeyOf(part.text), level: answer?.level ?? 0, credit: answer?.credit ?? 0, meets: answer?.meets ?? 0 });
    });
    return { askId: ask.id, status: 'read', attempts, english: person.english, lines };
}
//# sourceMappingURL=icp-rank.js.map